import * as Sentry from "@sentry/react"
import { FunctionsHttpError } from "@supabase/supabase-js"

import { Application } from "@/contexts/ApplicationContext"
import supabase from "@/supabase"

// No need for window.magicLink extension anymore

// Add TypeScript declaration for import.meta.env
declare global {
  interface ImportMeta {
    env: Record<string, string>
  }
}

/**
 * Start application process via cloud function
 * This will create/update applicant and application records and send a magic link
 */
export const startApplication = async (
  application: Application,
  redirectUrl: string,
): Promise<{
  success: boolean
  message: string
  applicationId?: string
  applicantId?: string
  status?: string
  applicationDate?: string
  isDuplicate?: boolean
  errorCode?: string
  progression?: { nextStage?: string }
}> => {
  try {
    // Get the function URL from environment variables or construct it
    const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/start-application`

    // Get the anon key for authorization
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

    if (!functionUrl || !anonKey) {
      throw new Error("Missing Supabase configuration")
    }

    // Call the cloud function
    const response = await fetch(functionUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify({
        application,
        redirectUrl,
      }),
    })

    // Parse the response
    const data = await response.json()

    if (!response.ok) {
      // Special handling for 409 Conflict (duplicate application)
      if (response.status === 409) {
        return {
          success: false,
          message: data.message || "You already have a recent application for this job.",
          status: data.status,
          applicationDate: data.applicationDate,
          applicationId: data.applicationId,
          isDuplicate: true,
          errorCode: data.errorCode,
          progression: data.progression,
        }
      }
      // For other errors
      throw new Error(data.message || "Failed to process application")
    }

    return {
      success: data.success,
      message: data.message,
      applicationId: data.applicationId,
      applicantId: data.applicantId,
      errorCode: data.errorCode,
      progression: data.progression,
    }
  } catch (error) {
    console.error("Error in startApplication:", error)

    Sentry.captureException(error, {
      extra: {
        context: "cloudFunctionService.startApplication",
        applicationData: JSON.stringify(application),
        redirectUrl,
      },
    })

    // Check if error contains application status information
    if (error && typeof error === "object" && "applicationDate" in error && "status" in error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to process application",
        applicationDate: error.applicationDate,
        status: error.status,
        isDuplicate: true,
      }
    }

    // Generic error
    return {
      success: false,
      message: error instanceof Error ? error.message : "Failed to process application",
    }
  }
}

/**
 * Send magic link via cloud function
 * This will generate and send a magic link to the specified email
 */
/**
 * Submit Driver On-road Test results via cloud function
 * This will save the assessment results and update the application stage
 */
export const submitDriverOnroadTest = async (
  applicationId: string,
  score: number,
  answers: Record<string, string | number | boolean | object>,
): Promise<{
  success: boolean
  message: string
  data?: {
    score?: number
    result?: string
    nextStage?: string
  }
  error?: string
}> => {
  try {
    const { data, error } = await supabase.functions.invoke(
      "assessment-submit-driver-onroad-test",
      {
        body: {
          applicationId,
          score,
          answers,
        },
      },
    )

    if (error) {
      const errorBody =
        error instanceof FunctionsHttpError ? await error.context.json().catch(() => ({})) : {}

      // Special handling for 409 Conflict (duplicate assessment)
      if (error instanceof FunctionsHttpError && error.context.status === 409) {
        return {
          success: false,
          message:
            errorBody.message || "An assessment has already been submitted for this application",
          error: errorBody.error || "DUPLICATE_ASSESSMENT",
        }
      }
      // For other errors
      throw new Error(errorBody.message || "Failed to submit Driver On-road Test assessment")
    }

    return {
      success: data.success,
      message: data.message,
      data: {
        score: data.data?.score,
        result: data.data?.result,
        nextStage: data.data?.nextStage,
      },
    }
  } catch (error) {
    console.error("Error in submitDriverOnroadTest:", error)

    Sentry.captureException(error, {
      extra: {
        context: "cloudFunctionService.submitDriverOnroadTest",
        applicationId,
        score,
      },
    })

    // Check if error is already a formatted response with error code
    if (error && typeof error === "object" && "error" in error) {
      return error as {
        success: boolean
        message: string
        error: string
      }
    }

    return {
      success: false,
      message:
        error instanceof Error ? error.message : "Failed to submit Driver On-road Test assessment",
    }
  }
}

export const sendMagicLink = async (
  email: string,
  applicationId: string,
  redirectUrl: string,
  firstName?: string,
): Promise<{
  success: boolean
  message: string
}> => {
  try {
    // Get the function URL from environment variables or construct it
    const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-magic-link`

    // Get the anon key for authorization
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

    if (!functionUrl || !anonKey) {
      throw new Error("Missing Supabase configuration")
    }

    // Call the cloud function
    const response = await fetch(functionUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify({
        email,
        firstName,
        applicationId,
        redirectUrl,
      }),
    })

    // Parse the response
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.message || "Failed to send magic link")
    }

    return {
      success: data.success,
      message: data.message,
    }
  } catch (error) {
    console.error("Error in sendMagicLink:", error)

    Sentry.captureException(error, {
      extra: {
        context: "cloudFunctionService.sendMagicLink",
        email,
        applicationId,
        hasFirstName: !!firstName,
      },
    })

    return {
      success: false,
      message: error instanceof Error ? error.message : "Failed to send magic link",
    }
  }
}

export const applicationLogin = async (
  email: string,
  applicationId: string,
  redirectUrl: string,
): Promise<{
  success: boolean
  message: string
  magicLink?: string
}> => {
  try {
    // Get the function URL from environment variables or construct it
    const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/application-login`

    // Get the anon key for authorization
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

    if (!functionUrl || !anonKey) {
      throw new Error("Missing Supabase configuration")
    }

    // Call the cloud function
    const response = await fetch(functionUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify({
        email,
        applicationId,
        redirectUrl,
      }),
    })

    // Parse the response
    const result = await response.json()

    if (!response.ok) {
      throw new Error(result.message || "Failed to get magic link")
    }

    return {
      success: result.success,
      message: result.message,
      magicLink: result.data.magicLink,
    }
  } catch (error) {
    console.error("Error in applicationLogin:", error)

    Sentry.captureException(error, {
      extra: {
        context: "cloudFunctionService.applicationLogin",
        email,
        applicationId,
      },
    })

    return {
      success: false,
      message: error instanceof Error ? error.message : "Failed to get magic link",
    }
  }
}
